// src/components/CourseLearning/ExternalEmbed.jsx
//
// Renders external link lessons ("external_reference", "external_media",
// "educational_game") inside a hardened iframe.
//
// Security model:
//  - Strict scheme allowlist: only absolute http(s) URLs are rendered. Anything
//    else (relative paths, javascript:, data:, file:, ftp:) is rejected.
//  - Per-subtype sandbox attribute with the minimum grants needed for the
//    expected content. No top-navigation / modals access is ever granted.
//  - referrerPolicy="no-referrer" so the course ID path is not leaked to the
//    embedded third party.
//
// NOTE: The URL is intentionally used as-is (no REACT_APP_CONTENT_URL prefix)
// because external lessons carry an absolute URL, unlike file lessons.
import React, { useEffect, useMemo } from 'react';
import Unavailable from '../Common/Unavailable';
import './ExternalEmbed.css';

const EXTERNAL_TYPES = ['external_reference', 'external_media', 'educational_game'];

// Minimum-viable sandbox grants per subtype. Deliberately strict: we grant only
// what the expected content needs and never allow navigating the top window.
const SANDBOX_POLICY = {
    // Reference docs / readers (PDFs, Google Docs, etc): scripts + same-origin
    // to let the document load and render, nothing more.
    external_reference: 'allow-scripts allow-same-origin',
    // Externally hosted media (YouTube, Vimeo, etc): needs presentation/popups
    // for fullscreen/embed and forms for some players.
    external_media: 'allow-scripts allow-same-origin allow-presentation allow-popups allow-forms',
    // Educational games: interactive content may need forms, popups and
    // downloads, plus presentation for fullscreen.
    educational_game:
        'allow-scripts allow-same-origin allow-forms allow-popups allow-downloads allow-presentation',
};

// FULLSCREEN_TYPES: subtypes that get allowFullScreen on the iframe.
const FULLSCREEN_TYPES = ['external_media', 'educational_game'];

function toSafeUrl(value) {
    if (typeof value !== 'string' || value.trim() === '') return null;
    let parsed;
    try {
        parsed = new URL(value.trim());
    } catch {
        return null;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed.toString();
}

const ExternalEmbed = ({ url, type = 'external_reference', onView }) => {
    const safeUrl = useMemo(() => toSafeUrl(url), [url]);

    const isKnownType = EXTERNAL_TYPES.includes(type);
    const sandbox = isKnownType ? SANDBOX_POLICY[type] : SANDBOX_POLICY.external_reference;
    const allowFullScreen = FULLSCREEN_TYPES.includes(type);

    // Mark the lesson as viewed/completed as soon as it is rendered, but only
    // when the embedded URL is actually safe to load.
    useEffect(() => {
        if (safeUrl && typeof onView === 'function') {
            onView();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeUrl]);

    if (!safeUrl) {
        return <Unavailable />;
    }

    return (
        <div className="external-embed">
            <iframe
                className="external-embed-frame"
                src={safeUrl}
                sandbox={sandbox}
                referrerPolicy="no-referrer"
                title={type}
                loading="lazy"
                allowFullScreen={allowFullScreen}
            />
        </div>
    );
};

export default ExternalEmbed;
